import type { ReactNode } from 'react'

import { Button } from '@/components/primitives/button'
import { cn } from '@/lib/cn'

export interface DrawerStepProgressProps<Step extends string> {
  ariaLabel: string
  canSelectStep?: (step: Step, index: number) => boolean
  className?: string
  currentStep: Step
  getStepLabel?: (step: Step) => ReactNode
  onStepChange?: (step: Step) => void
  steps: readonly Step[]
}

export function DrawerStepProgress<Step extends string>({
  ariaLabel,
  canSelectStep,
  className,
  currentStep,
  getStepLabel,
  onStepChange,
  steps,
}: DrawerStepProgressProps<Step>) {
  const currentIndex = steps.indexOf(currentStep)

  return (
    <div className={cn('max-w-full', className)}>
      <ol
        aria-label={ariaLabel}
        className="grid"
        role="navigation"
        style={{ gridTemplateColumns: `repeat(${String(steps.length)}, minmax(0, 1fr))` }}
      >
        {steps.map((step, index) => {
          const selectable = canSelectStep?.(step, index) ?? true
          const selected = step === currentStep
          const completed = index < currentIndex

          return (
            <li
              className={cn(
                'relative min-w-0',
                index < steps.length - 1 &&
                  "after:absolute after:top-[11px] after:left-[calc(50%+16px)] after:h-px after:w-[calc(100%-32px)] after:content-['']",
                index < currentIndex
                  ? 'after:bg-[var(--color-primary)]'
                  : 'after:bg-[var(--color-border)]',
              )}
              key={step}
            >
              <button
                aria-current={selected ? 'step' : undefined}
                className={cn(
                  'relative z-10 flex w-full min-w-0 flex-col items-center gap-1.5 rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]',
                  selectable ? 'cursor-pointer' : 'cursor-not-allowed',
                )}
                disabled={!selectable}
                onClick={() => onStepChange?.(step)}
                type="button"
              >
                <span
                  className={cn(
                    'flex size-[23px] shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold transition-colors',
                    selected
                      ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                      : completed
                        ? 'border-[var(--color-primary)] bg-white text-[var(--color-primary)]'
                        : 'border-[var(--color-border)] bg-white text-[var(--color-text-subtle)]',
                  )}
                >
                  {completed ? (
                    <svg aria-hidden className="size-3" fill="none" viewBox="0 0 12 12">
                      <path
                        d="m2.4 6.1 2.1 2.1 5.1-5"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                      />
                    </svg>
                  ) : (
                    index + 1
                  )}
                </span>
                {getStepLabel ? (
                  <span
                    className={cn(
                      'block w-full truncate px-1 text-center text-[11px] leading-4 transition-colors',
                      selected
                        ? 'font-semibold text-[var(--color-text)]'
                        : selectable
                          ? 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
                          : 'text-[var(--color-text-subtle)]',
                    )}
                  >
                    {getStepLabel(step)}
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

export interface DrawerStepTitleProps {
  action?: ReactNode
  className?: string
  compact?: boolean
  title: ReactNode
}

export function DrawerStepTitle({
  action,
  className,
  compact = false,
  title,
}: DrawerStepTitleProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4', compact ? 'mb-2' : 'mb-5')}>
      <h3 className={cn('text-base leading-6 font-bold text-[var(--color-text)]', className)}>
        {title}
      </h3>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

export interface DrawerWizardFooterProps {
  backLabel: ReactNode
  backDisabled?: boolean
  canGoBack: boolean
  isLoading?: boolean
  nextLabel: ReactNode
  nextDisabled?: boolean
  onBack: () => void
  onNext: () => void
}

export function DrawerWizardFooter({
  backLabel,
  backDisabled,
  canGoBack,
  isLoading,
  nextLabel,
  nextDisabled,
  onBack,
  onNext,
}: DrawerWizardFooterProps) {
  const backButtonDisabled = [backDisabled, isLoading].some(Boolean)

  return (
    <div className="flex w-full items-center justify-between gap-3">
      {canGoBack ? (
        <Button
          className="h-[38px] min-w-[72px] rounded-md border-[var(--color-control-border)] px-4 font-normal"
          disabled={backButtonDisabled}
          onClick={onBack}
          size="sm"
          variant="secondary"
        >
          {backLabel}
        </Button>
      ) : (
        <span aria-hidden />
      )}
      <Button
        className="h-[38px] min-w-[88px] rounded-md px-4 font-normal"
        disabled={nextDisabled}
        isLoading={isLoading}
        onClick={onNext}
        size="sm"
      >
        {nextLabel}
      </Button>
    </div>
  )
}

export interface DrawerTabItem<Tab extends string> {
  id: Tab
  label: ReactNode
  panelId: string
  tabId: string
}

export interface DrawerTabsProps<Tab extends string> {
  activeTab: Tab
  ariaLabel: string
  className?: string
  onTabChange: (tab: Tab) => void
  placement?: 'body' | 'header'
  tabs: readonly DrawerTabItem<Tab>[]
}

export function DrawerTabs<Tab extends string>({
  activeTab,
  ariaLabel,
  className,
  onTabChange,
  placement = 'header',
  tabs,
}: DrawerTabsProps<Tab>) {
  return (
    <div
      className={cn(
        '-mx-6 border-t border-[var(--color-divider)]',
        placement === 'header' ? '-mb-5' : 'border-b',
        className,
      )}
    >
      <div
        aria-label={ariaLabel}
        className="flex min-w-0 gap-8 overflow-x-auto px-6 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
      >
        {tabs.map((tab) => {
          const selected = activeTab === tab.id

          return (
            <button
              aria-controls={tab.panelId}
              aria-selected={selected}
              className={cn(
                'h-12 shrink-0 cursor-pointer border-b-2 px-1 text-sm font-medium transition-colors',
                'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)]',
                selected
                  ? 'border-[var(--color-primary)] font-semibold text-[var(--color-text)]'
                  : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
              )}
              id={tab.tabId}
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              role="tab"
              type="button"
            >
              {tab.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}
